import Vue from 'vue';
import { BoardInputHandler, IBoardTransform } from 'js/crossword/input/BoardInputHandler';
import { CellType, ICell, IClue } from 'js/crossword/types';
import { KeyboardInputCatcher } from 'js/crossword/input/KeyboardInputCatcher';
import { Puzzle } from 'js/crossword/Puzzle';
import OS from 'framework/utils/OS';
import BoardCell from "./BoardCell";

interface IData {
  inputHandler: BoardInputHandler | null;
  keyboardInput: KeyboardInputCatcher | null;
  boardTransform: IBoardTransform;
}

export default Vue.extend({
  data(): IData {
    return {
      inputHandler: null,
      keyboardInput: null,
      boardTransform: {
        centerOffsetX: 0,
        width: 100,
        height: 100,
        moved: false,
        pinching: false,
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
      },
    };
  },
  props: {
    puzzle: Puzzle,
  },
  mounted() {
    const boardContainer = this.$refs.boardContainer as HTMLDivElement;

    this.keyboardInput = new KeyboardInputCatcher();
    this.keyboardInput.onLetterPressed.add((letter) => this.puzzle.fillCell(letter));
    this.keyboardInput.onBackspacePressed.add(() => this.puzzle.eraseCell());
    this.keyboardInput.onArrowPressed.add((keycode) => this.puzzle.handleArrowNavigation(keycode));
    this.keyboardInput.onSpacebarPressed.add(() => this.puzzle.handleSpacebar());
    this.keyboardInput.onTabPressed.add(() => this.puzzle.changeClue(1));
    this.keyboardInput.onShiftTabPressed.add(() => this.puzzle.changeClue(-1));
    this.keyboardInput.onEnterPressed.add(() => this.puzzle.changeClue(1));
    this.keyboardInput.onShiftEnterPressed.add(() => this.puzzle.changeClue(-1));

    this.inputHandler = new BoardInputHandler(boardContainer, this.boardTransform);
  },
  watch: {
    boardTransform: {
      handler(newValue) {
        const boardContainer = this.$refs.boardContainer as HTMLDivElement;
        const centerOffsetXZoomed = newValue.centerOffsetX / newValue.zoom;
        boardContainer.style.transform = `scale(${newValue.zoom}) translate(${newValue.offsetX + centerOffsetXZoomed}px, ${newValue.offsetY}px)`;
        this.updateMobileInputPosition();
      },
      deep: true,
    },
    currentClue(clue) {
      this.focusOnCells(clue.cells);
    },
    'puzzle.data.currentCellId': {
      handler(newValue) {
        this.updateMobileInputPosition();
      },
    },
  },
  computed: {
    currentClue(): IClue {
      return this.puzzle.data.clues.find((c) => c.id === this.puzzle.data.currentClueId)!;
    },
    boardGridLayout(): string {
      return `
        grid-template-columns: repeat(${this.puzzle.data.columns}, 1fr);
        grid-template-rows: repeat(${this.puzzle.data.rows}, 1fr);
      `;
    },
  },
  methods: {
    updateMobileInputPosition() {
      // try {
        const cell = this.$refs[`cell_${this.puzzle.data.currentCellId}`][0];
        if (!cell) {
          return;
        }
        const boundingRect = cell.$el.getBoundingClientRect();
        const mobileInput = document.getElementById('mobileTextInput')!;
        mobileInput.style.top = `${boundingRect.top}px`;
        mobileInput.style.left = `${boundingRect.left}px`;
        mobileInput.style.width = `${boundingRect.width}px`;
        mobileInput.style.height = `${boundingRect.height}px`;
      // } catch (e) {
      //   console.warn(e);
      // }
    },
    focusOnCells(cellsIds: string[]) {
      if (this.boardTransform.zoom === 1) return;

      const focusRect = this.getFocusRect(cellsIds);

      const boardContainer = this.$refs.boardContainer as HTMLDivElement;

      const boardCenterX = this.boardTransform.width * 0.5;
      const boardCenterY = this.boardTransform.height * 0.5;

      focusRect.x -= boardCenterX;
      focusRect.y -= boardCenterY;

      const zoom = this.boardTransform.zoom;

      boardContainer.style.transition = 'transform 490ms';
      setTimeout(() => {
        boardContainer.style.transition = '';
      }, 490);

      if (focusRect.width > this.boardTransform.width / zoom || focusRect.height > this.boardTransform.height / zoom) {
        this.boardTransform.zoom = Math.max(
          1,
          Math.min(this.boardTransform.width / focusRect.width, this.boardTransform.height / focusRect.height),
        );
      }
      this.inputHandler!.setOffset(-(focusRect.x + focusRect.width * 0.5), -(focusRect.y + focusRect.height * 0.5));
    },
    getFocusRect(cellIds: string[]) {
      const columns: number = this.puzzle.data.columns;
      const rows: number = this.puzzle.data.rows;
      const cellWidth = this.boardTransform.width / columns;
      const cellHeight = this.boardTransform.height / rows;

      const pointA = { x: Infinity, y: Infinity };
      const pointB = { x: -Infinity, y: -Infinity };

      this.puzzle.data.cells
        .filter((cell) => cellIds.includes(cell.id))
        .forEach((cell) => {
          const { x, y } = cell;
          pointA.x = Math.min(x * cellWidth, pointA.x);
          pointA.y = Math.min(y * cellHeight, pointA.y);
          pointB.x = Math.max((x + 1) * cellWidth, pointB.x);
          pointB.y = Math.max((y + 1) * cellHeight, pointB.y);
        });

      if (pointA.x > 0) pointA.x -= cellWidth;
      if (pointA.y > 0) pointA.y -= cellHeight;
      if (pointB.x <= columns * cellWidth) pointB.x += cellWidth;
      if (pointB.y <= rows * cellHeight) pointB.y += cellHeight;

      return { x: pointA.x, y: pointA.y, width: pointB.x - pointA.x, height: pointB.y - pointA.y };
    },
    zoomIn() {
      this.inputHandler!.changeZoom(0.2);
    },
    zoomOut() {
      this.inputHandler!.changeZoom(-0.2);
    },
    handleCellClick(cell: ICell) {
      if (this.boardTransform.moved) return;
      if (cell.type !== CellType.Regular) return;

      this.puzzle.handleCellClick(cell.id);
    
      if (cell.type !== CellType.Regular) return;
      if (OS.iPad || OS.android || !OS.desktop) {
        document.getElementById('mobileTextInput')!.focus();
      }
    },
    handleClueClick(clueId: string) {
      this.puzzle.setCurrentClue(clueId);
    },
    resize(width: number, height: number, centerOffsetX: number = 0) {
      this.boardTransform.width = width;
      this.boardTransform.height = height;
      this.boardTransform.centerOffsetX = centerOffsetX;
      const boardContainer = this.$refs.boardContainer as HTMLDivElement;
      boardContainer.style.width = `${width - 1}px`;
      boardContainer.style.height = `${height - 1}px`;
    },
  },
  render(){
    const { cells, currentCellId, columns, rows } = this.puzzle.data;
    return(
    <div 
      class={{
        "board": true,
        "board--transformAnimated": !this.boardTransform.moved && !this.boardTransform.pinching
      }}
      ref='boardContainer'
      style={this.boardGridLayout}
    >
      {cells.map((cell) => (
        <BoardCell
          onCellClicked={this.handleCellClick.bind(this)}
          onClueClicked={this.handleClueClick.bind(this)}
          currentClue={this.currentClue}
          currentCellId={currentCellId}
          cell={cell}
          puzzleColumns={columns}
          puzzleRows={rows}
          key={cell.id}
          ref={"cell_" + cell.id}
        />
      ))}
    </div>
    );
  },
});
